import React, { useEffect, useState } from "react";
import { ChevronLeft, LogOut, UserCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { SidebarLogo } from "./SidebarLogo";
import { SidebarMenu } from "./SidebarMenu";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { hasAnyPermission } from "@/permissions/rbac";
import { PERMISSIONS } from "@/permissions/constant";

const Sidebar = ({ isOpen }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const updateViewport = () => setIsDesktop(mediaQuery.matches);

    updateViewport();
    mediaQuery.addEventListener("change", updateViewport);
    return () => mediaQuery.removeEventListener("change", updateViewport);
  }, []);


  const isCollapsed = collapsed && isDesktop;

  const hasUserPermission = hasAnyPermission(
    [PERMISSIONS.SUPER_ADMIN_MANAGE, PERMISSIONS.USER_VIEW, PERMISSIONS.USER_CREATE, PERMISSIONS.USER_UPDATE, PERMISSIONS.USER_DELETE],
    user.permissions,
  );

  const userRole = user?.role?.name || user?.role || "";

  const onHandleLogout = async () => {
    try {
      const response = await logout();

      if (response.success) {
        toast.success("See you again!");
        navigate("/login");
      } else {
        toast.error(response.error);
      }
    } catch (err) {
      toast.error(err.message || "Something went wrong.");
    }
  };

  const onHandleUserProfile = () => {
    // This sidebar only ever renders inside the admin shell, so any tenant
    // user reaching it — pending or not, they hold a real role from their
    // first login — belongs on the in-shell profile page.
    navigate(user?.isSuperAdmin ? "/super-admin/user-profile" : "/admin/user-profile");
  };

  return (
    <div
      className={cn(
        "fixed inset-y-0 left-0 z-30 h-screen flex flex-col flex-shrink-0",
        "bg-background-secondary text-text-primary",
        "px-2 py-3 border-r border-border-primary",
        "transition-all duration-300",
        "lg:static lg:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full",
        isCollapsed ? "w-[60px]" : "w-44",
      )}
    >
      {/* Top / navigation area */}
      <div className="flex flex-col flex-1 min-h-0">
        <SidebarLogo collapsed={isCollapsed} />

        {/* Collapse button */}
        <Button
          type="button"
          onClick={() => setCollapsed((prev) => !prev)}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={cn(
            "absolute hidden lg:flex",
            "z-10 top-[18px] -right-[11px]",
            "w-[22px] h-[22px] p-0",
            "items-center justify-center",
            "bg-background-tertiary hover:bg-background-secondary",
            "border border-border-primary rounded-md",
            "transition-colors duration-150"
          )}
        >
          <ChevronLeft
            size={12}
            className={cn(
              "text-text-secondary transition-transform duration-300",
              isCollapsed && "rotate-180"
            )}
          />
        </Button>

        <div className="border-b border-border-primary mb-3" />

        {/* 
          Scrollable navigation area.
          The scrollbar is completely hidden while mouse wheel,
          trackpad and touch scrolling continue to work normally.
        */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-hide">
          <SidebarMenu
            collapsed={isCollapsed}
            permissions={user?.permissions || []}
          />
        </div>
      </div>

      {/* User / logout area */}
      <div
        className={cn(
          "flex flex-col gap-2.5 mt-2 shrink-0",
          isCollapsed && "mx-auto"
        )}
      >
        <Button
          type="button"
          disabled={!hasUserPermission}
          onClick={onHandleUserProfile}
          aria-label={`Open profile for ${user?.name || "current user"}`}
          title={isCollapsed ? "Open profile" : undefined}
          className={cn(
            "flex items-center gap-2 ml-[2px]",
            "rounded-md text-left",
            "transition-colors",
            "focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-ring",
            isCollapsed
              ? "justify-center w-8 h-8 p-0"
              : "w-full px-1 py-1 hover:bg-background-tertiary"
          )}
        >
          <div className="bg-[#787EE9]/70 p-1.5 rounded-md flex-shrink-0">
            <UserCircle size={14} className="text-white" />
          </div>

          {!isCollapsed && (
            <div className="min-w-0 overflow-hidden">
              <p className="text-[11px] text-text-primary truncate leading-tight">
                {user?.name || "Unknown"}
              </p>

              {userRole && (
                <p className="text-[10px] text-text-secondary truncate capitalize leading-tight mt-0.5">
                  {userRole.replace(/_/g, " ")}
                </p>
              )}
            </div>
          )}
        </Button>

        <Button
          type="button"
          onClick={onHandleLogout}
          className={cn(
            "group flex items-center rounded-md",
            "transition-all duration-200",
            "bg-background-tertiary hover:bg-red-500/10",
            isCollapsed
              ? "justify-center w-8 h-8 p-0"
              : "gap-2 px-2 py-1.5 w-full"
          )}
        >
          <div className="bg-red-500/10 p-1 rounded-md flex-shrink-0">
            <LogOut size={13} className="text-red-500" />
          </div>

          {!isCollapsed && (
            <p className="text-[11px] text-text-primary truncate leading-tight">
              Logout
            </p>
          )}
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;